/**
 * Hook for location search with debouncing
 */

import { useState, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';
import { api } from '../api';
import type { LocationSearchResult } from '../types';

interface UseLocationSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
}

interface UseLocationSearchResult {
  query: string;
  setQuery: (query: string) => void;
  results: LocationSearchResult[];
  isLoading: boolean;
  error: string | null;
  hasSearched: boolean;
  selectLocation: (location: LocationSearchResult) => void;
  selectedLocation: LocationSearchResult | null;
  clearSelection: () => void;
}

export function useLocationSearch(
  options: UseLocationSearchOptions = {}
): UseLocationSearchResult {
  const { debounceMs = 300, minQueryLength = 2 } = options;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationSearchResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced search effect
  useEffect(() => {
    if (query.length < minQueryLength) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      setError(null);

      try {
        const searchResults = await api.searchLocations(query);
        setResults(searchResults);
        setHasSearched(true);
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 503) {
          setError('Сервис поиска временно недоступен, попробуйте через несколько секунд');
        } else {
          setError(err instanceof Error ? err.message : 'Ошибка поиска');
        }
        setResults([]);
        setHasSearched(true);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [query, debounceMs, minQueryLength]);

  const selectLocation = useCallback((location: LocationSearchResult) => {
    setSelectedLocation(location);
    setQuery(location.displayNameShort);
    setResults([]);
    setHasSearched(false);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLocation(null);
    setQuery('');
    setResults([]);
    setHasSearched(false);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    hasSearched,
    selectLocation,
    selectedLocation,
    clearSelection,
  };
}

export default useLocationSearch;
